import { useReviewPeriodLock } from '../workspace/reviewPeriod';
import React, { useState } from 'react';
import { getTenantToday } from '../../../utils/dateUtils';
import styled from 'styled-components';
import axios from 'axios';
import { apiErrorMessage } from '../api/abfApi';
import { plural } from '../presentation/format';
import { Button, Card, CardHead, INK, Row, fieldBase } from '../onboarding/review/reviewStyles';
import { DateField } from '../../../components/shared/DateField';

/**
 * Slice 4 — the actions the Monitoring tab was missing.
 *
 * Slice 4 built ten command endpoints and the workspace wired three. Waivers, exception
 * disposition, ownership, due dates, keyed certificates and thresholds were reachable
 * only by API call, so six of the acceptance steps could not be performed at a screen.
 * Two of them had a UI on a card that a de-duplication pass removed, believing the new
 * read-only panel superseded it; it superseded the tables, not the buttons.
 *
 * Each form here is a governed decision, so each one states the rule it is subject to in
 * the words the server uses, and lets the server refuse rather than guessing client-side.
 * Nothing is disabled on a rule the server does not enforce (FRD section 3.5).
 */

const ROOT = '/api/credit/abf/round2';

/** The dispositions a person may choose. System-set values such as APPROVED_WAIVER are not here. */
const CLASSIFICATIONS = [
  { value: 'INVESTIGATED', label: 'Investigated' },
  { value: 'DATA_QUALITY', label: 'Data quality' },
  { value: 'TIMING', label: 'Timing' },
  { value: 'ACCEPTED_RISK', label: 'Accepted risk' },
  { value: 'REMEDIATED', label: 'Remediated' },
];

// ---------------------------------------------------------------------------

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.65rem;
  margin-bottom: 0.8rem;
`;

const Field = styled.label<{ $wide?: boolean }>`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.28rem;
  color: ${INK.muted};
  font-size: 0.64rem;
  font-weight: 750;
  ${p => p.$wide ? 'grid-column: 1 / -1;' : ''}

  input, select, textarea { ${fieldBase} }
  textarea { min-height: 4.4rem; resize: vertical; }
  small { color: ${INK.faint}; font-weight: 600; font-size: 0.62rem; }
`;

const Failure = styled.p`
  margin: 0 0 0.7rem;
  padding: 0.55rem 0.65rem;
  border: 1px solid rgba(248, 113, 113, 0.4);
  border-radius: 8px;
  background: rgba(127, 29, 29, 0.18);
  color: #fecaca;
  font-size: 0.72rem;
  line-height: 1.5;
`;

const Rule = styled.p`
  margin: 0 0 0.75rem;
  color: ${INK.faint};
  font-size: 0.7rem;
  line-height: 1.55;
`;

/** One place for submit state, so every form fails the same way and says so in place. */
function useSubmit(onDone: (message: string) => void) {
  const [busy, setBusy] = useState(false);
  useReviewPeriodLock(busy);
  const [error, setError] = useState<string | null>(null);
  const submit = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true); setError(null);
    try {
      await action();
      onDone(success);
    } catch (e) {
      setError(apiErrorMessage(e, 'The action could not be completed.'));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

// ---------------------------------------------------------------------------
// Waivers
// ---------------------------------------------------------------------------

export const WaiverRequestForm: React.FC<{
  facilityId: number;
  covenant: any;
  periods: any[];
  businessDate?: string;
  onDone: (message: string) => void;
  onCancel: () => void;
}> = ({ facilityId, covenant, periods, businessDate, onDone, onCancel }) => {
  const result = covenant.result || {};
  // F-S4-025: a covenant is tested on its determination date, which is usually in the
  // future. Starting the waiver on the test date therefore produced a waiver the server
  // correctly refused to activate ("a future-dated waiver cannot be activated before its
  // effective date") — approved by nobody, blocking nothing, with no hint why. The start
  // defaults to the test date only when that date has already passed.
  const today = businessDate || getTenantToday();
  const testDate = result.test_date ? String(result.test_date).slice(0, 10) : today;
  const [effectiveFrom, setEffectiveFrom] = useState(testDate <= today ? testDate : today);
  const [waiverType, setWaiverType] = useState('TEMPORARY');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [scopeFrom, setScopeFrom] = useState(result.period_id ? String(result.period_id) : '');
  const [scopeTo, setScopeTo] = useState(result.period_id ? String(result.period_id) : '');
  const [amendmentRef, setAmendmentRef] = useState('');
  const [conditions, setConditions] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [notes, setNotes] = useState('');
  const { busy, error, submit } = useSubmit(onDone);

  const conditionList = conditions.split('\n').map(t => t.trim()).filter(Boolean).map(text => ({ text, dueDate: null }));

  return <Card style={{ marginTop: 12, borderColor: INK.lineStrong }}>
    <CardHead>
      <div>
        <h2>Request a waiver on {covenant.covenant_name}</h2>
        <p>A waiver suppresses the contractual consequence of a breach. It does not change the
          test result, and it ends on a date.</p>
      </div>
    </CardHead>
    <Rule>
      A temporary waiver must carry an end date. A permanent one is an amendment in substance,
      so it names the activated facility version that changed the covenant. The person who
      requests it cannot approve it.
    </Rule>
    {error && <Failure>{error}</Failure>}
    <Fields>
      <Field>Starts on
        <DateField value={effectiveFrom} today={today} onChange={setEffectiveFrom} />
        <small>{testDate > today
          ? `The covenant was tested on ${testDate}, which has not happened yet. A waiver cannot be approved before it starts, so this defaults to today.`
          : 'A waiver cannot be approved before it starts.'}</small>
      </Field>
      <Field>Type
        <select value={waiverType} onChange={e => setWaiverType(e.target.value)}>
          <option value="TEMPORARY">Temporary</option>
          <option value="PERMANENT">Permanent (by amendment)</option>
        </select>
      </Field>
      {waiverType === 'TEMPORARY' && <Field>Ends on
        <DateField value={effectiveTo} min={effectiveFrom || undefined} minLabel="the start date" today={today} onChange={setEffectiveTo} />
        <small>The consequence returns on this date unless it is extended.</small>
      </Field>}
      {waiverType === 'PERMANENT' && <Field>Amendment reference
        <input value={amendmentRef} onChange={e => setAmendmentRef(e.target.value)}
          placeholder="Amendment that changed this covenant" />
        <small>A permanent waiver without an activated amendment is refused.</small>
      </Field>}
      <Field>Scope from period
        <select value={scopeFrom} onChange={e => setScopeFrom(e.target.value)}>
          <option value="">Not scoped to a period</option>
          {periods.map(p => <option key={p.id} value={p.id}>{p.period_label}</option>)}
        </select>
      </Field>
      <Field>Scope to period
        <select value={scopeTo} onChange={e => setScopeTo(e.target.value)}>
          <option value="">Same as from</option>
          {periods.map(p => <option key={p.id} value={p.id}>{p.period_label}</option>)}
        </select>
      </Field>
      <Field $wide>Conditions
        <textarea value={conditions} onChange={e => setConditions(e.target.value)}
          placeholder={'One condition per line, for example:\nBorrower delivers a remediation plan\nExcess availability restored above the floor'} />
        <small>Each line is recorded as its own condition on the waiver.</small>
      </Field>
      <Field>Source reference
        <input value={sourceRef} onChange={e => setSourceRef(e.target.value)}
          placeholder="Waiver letter or approval reference" />
      </Field>
      <Field $wide>Rationale
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Why this breach should be waived, and on what basis." />
      </Field>
    </Fields>
    <Row>
      <Button type="button" $variant="primary" disabled={busy || !notes.trim()}
        onClick={() => void submit(() => axios.post(`${ROOT}/facilities/${facilityId}/covenants/waivers`, {
          covenantDefinitionId: covenant.id,
          covenantRunId: result.id ?? null,
          effectiveFrom: effectiveFrom || null,
          effectiveTo: waiverType === 'TEMPORARY' ? (effectiveTo || null) : null,
          waiverType,
          scopeFromPeriodId: scopeFrom ? Number(scopeFrom) : null,
          scopeToPeriodId: scopeTo ? Number(scopeTo) : (scopeFrom ? Number(scopeFrom) : null),
          amendmentReference: amendmentRef.trim() || null,
          conditionList,
          sourceClause: covenant.source_clause ?? null,
          sourceDocumentJobId: sourceRef.trim() || null,
          notes: notes.trim(),
        }), `Waiver requested on ${covenant.covenant_name}. It needs a different actor to approve it.`)}>
        {busy ? 'Requesting…' : 'Request waiver'}
      </Button>
      <Button type="button" $variant="quiet" disabled={busy} onClick={onCancel}>Cancel</Button>
    </Row>
  </Card>;
};

export const WaiverDecision: React.FC<{
  facilityId: number;
  waiver: any;
  onDone: (message: string) => void;
  onCancel: () => void;
}> = ({ facilityId, waiver, onDone, onCancel }) => {
  const [notes, setNotes] = useState('');
  const { busy, error, submit } = useSubmit(onDone);
  const post = (approve: boolean) => axios.post(
    `${ROOT}/facilities/${facilityId}/covenants/waivers/${waiver.id}/review`, { approve, notes: notes.trim() });

  return <Card style={{ marginTop: 12, borderColor: INK.lineStrong }}>
    <CardHead>
      <div>
        <h2>Decide the waiver on {waiver.covenant_name}</h2>
        <p>Requested by {waiver.requested_by}. Approving it lifts the consequences the breach
          applied, until the waiver ends.</p>
      </div>
    </CardHead>
    <Rule>
      Rejecting leaves the breach and its consequences in force. Either way the decision is
      recorded against your name.
    </Rule>
    {error && <Failure>{error}</Failure>}
    <Fields>
      <Field $wide>Decision evidence
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="What you reviewed, and why this is the right decision." />
      </Field>
    </Fields>
    <Row>
      <Button type="button" $variant="primary" disabled={busy || !notes.trim()}
        onClick={() => void submit(() => post(true),
          `Waiver approved on ${waiver.covenant_name}. Its consequences are lifted until it ends.`)}>
        {busy ? 'Recording…' : 'Approve waiver'}
      </Button>
      <Button type="button" $variant="danger" disabled={busy || !notes.trim()}
        onClick={() => void submit(() => post(false),
          `Waiver rejected. The breach and its consequences remain in force.`)}>
        Reject
      </Button>
      <Button type="button" $variant="quiet" disabled={busy} onClick={onCancel}>Cancel</Button>
    </Row>
  </Card>;
};

// ---------------------------------------------------------------------------
// Exceptions
// ---------------------------------------------------------------------------

export const ExceptionActions: React.FC<{
  facilityId: number;
  exception: any;
  onDone: (message: string) => void;
  onCancel: () => void;
}> = ({ facilityId, exception, onDone, onCancel }) => {
  const [owner, setOwner] = useState(exception.owner_username || '');
  const [dueDate, setDueDate] = useState(String(exception.due_at || '').slice(0, 10));
  const [dueReason, setDueReason] = useState('');
  // F-S4-031: seeding the select from the record's existing classification put a value in
  // state that was not one of this select's options, so the control displayed one thing and
  // submitted another. Ridgeline's exception still carried APPROVED_WAIVER from a waiver
  // that has since expired, and that is what got written when the operator submitted
  // without touching the dropdown. An out-of-list value is now offered explicitly rather
  // than held invisibly.
  const [classification, setClassification] = useState(
    CLASSIFICATIONS.some(c => c.value === exception.classification) ? exception.classification : 'INVESTIGATED');
  const [resolution, setResolution] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const { busy, error, submit } = useSubmit(onDone);

  const pending = exception.status === 'PENDING_APPROVAL';
  const needsTwo = exception.economically_material || exception.blocking;

  return <Card style={{ marginTop: 12, borderColor: INK.lineStrong }}>
    <CardHead>
      <div>
        <h2>{exception.title}</h2>
        <p>Due {String(exception.due_at || '').slice(0, 10) || 'not set'} on the
          {' '}{String(exception.due_date_basis || 'contract calendar').replace(/_/g, ' ').toLowerCase()}.
          {exception.holdsFunding ? ' This item holds funding.' : ''}</p>
      </div>
    </CardHead>
    <Rule>
      {pending
        ? `${exception.proposed_by} proposed a resolution. The rule is that they cannot approve it themselves.`
        : needsTwo
          ? 'This item is material or holds funding, so a proposed resolution goes to a second actor rather than closing here.'
          : 'This item is neither material nor blocking, so a resolution closes it in one step.'}
    </Rule>
    {error && <Failure>{error}</Failure>}

    {pending && <>
      <Fields>
        <Field $wide>Proposed resolution
          <textarea value={exception.proposed_resolution || ''} readOnly />
        </Field>
        <Field $wide>Independent review evidence
          <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
            placeholder="What you checked, and why you accept or reject this resolution." />
        </Field>
      </Fields>
      <Row>
        <Button type="button" $variant="primary" disabled={busy || !reviewNotes.trim()}
          onClick={() => void submit(() => axios.post(
            `${ROOT}/facilities/${facilityId}/exceptions/${exception.id}/review`,
            { approve: true, notes: reviewNotes.trim() }), 'Exception resolution approved.')}>
          {busy ? 'Recording…' : 'Approve resolution'}
        </Button>
        <Button type="button" $variant="danger" disabled={busy || !reviewNotes.trim()}
          onClick={() => void submit(() => axios.post(
            `${ROOT}/facilities/${facilityId}/exceptions/${exception.id}/review`,
            { approve: false, notes: reviewNotes.trim() }),
            'Exception resolution rejected and returned to the queue.')}>
          Reject
        </Button>
        <Button type="button" $variant="quiet" disabled={busy} onClick={onCancel}>Close</Button>
      </Row>
    </>}

    {!pending && <>
      <Fields>
        <Field>Owner
          <input value={owner} onChange={e => setOwner(e.target.value)} placeholder="username" />
          <small>{exception.owner_assigned_by === 'SYSTEM:DEFAULT_OWNER'
            ? 'Currently the facility default.' : `Assigned by ${exception.owner_assigned_by || 'nobody yet'}.`}</small>
        </Field>
        <Field>Due date
          <DateField value={dueDate} onChange={setDueDate} />
          <small>Derived from the payment calendar. Moving it needs a reason.</small>
        </Field>
        <Field $wide>Reason for moving the due date
          <input value={dueReason} onChange={e => setDueReason(e.target.value)}
            placeholder="Only needed if you change the date" />
        </Field>
        <Field>Classification
          <select value={classification} onChange={e => setClassification(e.target.value)}>
            {CLASSIFICATIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          {exception.classification && !CLASSIFICATIONS.some(c => c.value === exception.classification) &&
            <small>Currently recorded as {String(exception.classification).replace(/_/g, ' ').toLowerCase()},
              which is set by the system rather than chosen here. Submitting replaces it.</small>}
        </Field>
        <Field $wide>{needsTwo ? 'Proposed resolution' : 'Resolution'}
          <textarea value={resolution} onChange={e => setResolution(e.target.value)}
            placeholder={needsTwo
              ? 'What should close this. A second actor approves it.'
              : 'What closed this.'} />
        </Field>
      </Fields>
      <Row>
        <Button type="button" disabled={busy || !owner.trim() || owner === exception.owner_username}
          onClick={() => void submit(() => axios.post(
            `${ROOT}/facilities/${facilityId}/exceptions/${exception.id}/owner`,
            { owner: owner.trim() }), `Owner set to ${owner.trim()}.`)}>
          Assign owner
        </Button>
        <Button type="button" disabled={busy || !dueDate || !dueReason.trim()}
          onClick={() => void submit(() => axios.post(
            `${ROOT}/facilities/${facilityId}/exceptions/${exception.id}/due-date`,
            { dueDate, reason: dueReason.trim() }), `Due date moved to ${dueDate}.`)}>
          Override due date
        </Button>
        <Button type="button" $variant="primary" disabled={busy || !resolution.trim()}
          onClick={() => void submit(() => axios.post(
            `${ROOT}/facilities/${facilityId}/exceptions/${exception.id}/classify`,
            { classification, resolution: resolution.trim(), notes: resolution.trim() }),
            needsTwo ? 'Resolution proposed for independent approval.' : 'Exception resolved.')}>
          {needsTwo ? 'Propose resolution' : 'Resolve'}
        </Button>
        <Button type="button" $variant="quiet" disabled={busy} onClick={onCancel}>Close</Button>
      </Row>
    </>}
  </Card>;
};

// ---------------------------------------------------------------------------
// Borrower certificate, keyed
// ---------------------------------------------------------------------------

export const CertificateForm: React.FC<{
  facilityId: number;
  covenants: any[];
  asOfDefault?: string;
  onDone: (message: string) => void;
  onCancel: () => void;
}> = ({ facilityId, covenants, asOfDefault, onDone, onCancel }) => {
  const [asOfDate, setAsOfDate] = useState(asOfDefault || '');
  const [certifiedBy, setCertifiedBy] = useState('');
  const [sourceReference, setSourceReference] = useState('');
  const [notes, setNotes] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [pages, setPages] = useState<Record<string, string>>({});
  const { busy, error, submit } = useSubmit(onDone);

  const lines = covenants
    .filter(c => (values[c.covenant_key] ?? '').trim() !== '')
    .map(c => ({
      covenantKey: c.covenant_key,
      reportedValue: Number(values[c.covenant_key]),
      sourcePage: pages[c.covenant_key]?.trim() || null,
      sourceSection: null,
      sourceClause: c.source_clause || null,
      note: null,
    }));

  return <Card style={{ marginTop: 12, borderColor: INK.lineStrong }}>
    <CardHead>
      <div>
        <h2>Record a borrower compliance certificate</h2>
        <p>What the borrower asserts about its own covenants. Each figure is compared with
          what we computed from the released tape, and never substituted for it.</p>
      </div>
    </CardHead>
    <Rule>
      The same record is written whether the certificate arrives as a document or is keyed
      here. A figure that differs from the computed one opens a variance to explain; the
      computed value still governs.
    </Rule>
    {error && <Failure>{error}</Failure>}
    <Fields>
      <Field>As of date
        <DateField value={asOfDate} onChange={setAsOfDate} />
      </Field>
      <Field>Certified by
        <input value={certifiedBy} onChange={e => setCertifiedBy(e.target.value)}
          placeholder="Officer who signed it" />
      </Field>
      <Field>Source reference
        <input value={sourceReference} onChange={e => setSourceReference(e.target.value)}
          placeholder="Certificate reference" />
        <small>Required for a keyed entry, in place of a document.</small>
      </Field>
      {covenants.map(c => <Field key={c.covenant_key}>{c.covenant_name}
        <input type="number" step="0.000001" value={values[c.covenant_key] ?? ''}
          onChange={e => setValues({ ...values, [c.covenant_key]: e.target.value })}
          placeholder="Borrower's figure" />
        <input style={{ marginTop: 4 }} value={pages[c.covenant_key] ?? ''}
          onChange={e => setPages({ ...pages, [c.covenant_key]: e.target.value })}
          placeholder="Page in the certificate" />
      </Field>)}
      <Field $wide>Notes
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Anything about this certificate worth recording." />
      </Field>
    </Fields>
    <Row>
      <Button type="button" $variant="primary"
        disabled={busy || !asOfDate || !sourceReference.trim() || lines.length === 0}
        onClick={() => void submit(() => axios.post(
          `${ROOT}/facilities/${facilityId}/compliance-certificates`, {
            asOfDate,
            sourceReference: sourceReference.trim(),
            certifiedBy: certifiedBy.trim() || null,
            notes: notes.trim() || null,
            lines,
          }), `Certificate recorded with ${plural(lines.length, 'covenant figure')} compared.`)}>
        {busy ? 'Recording…' : `Record certificate (${lines.length})`}
      </Button>
      <Button type="button" $variant="quiet" disabled={busy} onClick={onCancel}>Cancel</Button>
    </Row>
  </Card>;
};

// ---------------------------------------------------------------------------
// Surveillance thresholds
// ---------------------------------------------------------------------------

const METRICS = [
  { key: 'delinquency_30_pct', label: '30+ delinquency' },
  { key: 'delinquency_60_pct', label: '60+ delinquency' },
  { key: 'delinquency_90_pct', label: '90+ delinquency' },
  { key: 'net_loss_rate_pct', label: 'Net loss rate' },
  { key: 'default_rate_pct', label: 'Default rate' },
  { key: 'prepayment_rate_pct', label: 'Prepayment rate' },
];

export const ThresholdForm: React.FC<{
  facilityId: number;
  metricName?: string;
  existing?: any;
  onDone: (message: string) => void;
  onCancel: () => void;
}> = ({ facilityId, metricName, existing, onDone, onCancel }) => {
  const [metric, setMetric] = useState(metricName || existing?.metric_name || METRICS[0].key);
  const [level, setLevel] = useState(existing?.level_threshold != null ? String(existing.level_threshold) : '');
  const [delta, setDelta] = useState(existing?.delta_threshold_ppt != null ? String(existing.delta_threshold_ppt) : '');
  const [severity, setSeverity] = useState(existing?.severity || 'WARNING');
  const [rationale, setRationale] = useState('');
  const { busy, error, submit } = useSubmit(onDone);
  const label = METRICS.find(m => m.key === metric)?.label || metric;

  return <Card style={{ marginTop: 12, borderColor: INK.lineStrong }}>
    <CardHead>
      <div>
        <h2>Propose a threshold for {label}</h2>
        <p>A threshold is what makes a move in this metric raise a trigger instead of needing
          to be noticed.</p>
      </div>
    </CardHead>
    <Rule>
      A proposal is not in force until a different actor approves it. The value it replaces
      stays visible as superseded.
    </Rule>
    {error && <Failure>{error}</Failure>}
    <Fields>
      <Field>Metric
        <select value={metric} onChange={e => setMetric(e.target.value)} disabled={Boolean(metricName || existing)}>
          {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </Field>
      <Field>Level (%)
        <input type="number" step="0.0001" value={level} onChange={e => setLevel(e.target.value)} />
        <small>Fires when the metric passes this level.</small>
      </Field>
      <Field>Change (points)
        <input type="number" step="0.0001" value={delta} onChange={e => setDelta(e.target.value)} />
        <small>Optional. Fires on a move this large between tapes.</small>
      </Field>
      <Field>Severity
        <select value={severity} onChange={e => setSeverity(e.target.value)}>
          <option value="INFO">Info</option>
          <option value="WARNING">Warning</option>
          <option value="CRITICAL">Critical</option>
        </select>
      </Field>
      <Field $wide>Rationale
        <textarea value={rationale} onChange={e => setRationale(e.target.value)}
          placeholder="Why this level, and what changed to justify it." />
      </Field>
    </Fields>
    <Row>
      <Button type="button" $variant="primary" disabled={busy || level === '' || !rationale.trim()}
        onClick={() => void submit(() => axios.post(
          `${ROOT}/facilities/${facilityId}/surveillance-thresholds`, {
            metricName: metric,
            levelThreshold: Number(level),
            deltaThresholdPpt: delta === '' ? null : Number(delta),
            severity,
            rationale: rationale.trim(),
          }), `Threshold proposed for ${label}. It needs a different actor to approve it.`)}>
        {busy ? 'Proposing…' : 'Propose threshold'}
      </Button>
      <Button type="button" $variant="quiet" disabled={busy} onClick={onCancel}>Cancel</Button>
    </Row>
  </Card>;
};

export const ThresholdDecision: React.FC<{
  facilityId: number;
  threshold: any;
  onDone: (message: string) => void;
  onCancel: () => void;
}> = ({ facilityId, threshold, onDone, onCancel }) => {
  const [notes, setNotes] = useState('');
  const { busy, error, submit } = useSubmit(onDone);
  const post = (approve: boolean) => axios.post(
    `${ROOT}/facilities/${facilityId}/surveillance-thresholds/${threshold.id}/decision`,
    { approve, notes: notes.trim() });

  return <Card style={{ marginTop: 12, borderColor: INK.lineStrong }}>
    <CardHead>
      <div>
        <h2>Decide the proposed threshold</h2>
        <p>{threshold.metric_name} at {String(threshold.level_threshold)}, proposed by {threshold.proposed_by}.
          {threshold.rationale ? ` Their reason: ${threshold.rationale}` : ''}</p>
      </div>
    </CardHead>
    <Rule>The proposer cannot approve their own threshold.</Rule>
    {error && <Failure>{error}</Failure>}
    <Fields>
      <Field $wide>Decision evidence
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Why this level is or is not right." />
      </Field>
    </Fields>
    <Row>
      <Button type="button" $variant="primary" disabled={busy || !notes.trim()}
        onClick={() => void submit(() => post(true), 'Threshold approved and now in force.')}>
        {busy ? 'Recording…' : 'Approve threshold'}
      </Button>
      <Button type="button" $variant="danger" disabled={busy || !notes.trim()}
        onClick={() => void submit(() => post(false), 'Threshold rejected; the previous value stands.')}>
        Reject
      </Button>
      <Button type="button" $variant="quiet" disabled={busy} onClick={onCancel}>Cancel</Button>
    </Row>
  </Card>;
};
